import { MAPBOX_TOKEN } from "../config/mapbox";
import {
  EMPTY_POIS_BY_CATEGORY,
  Poi,
  PoiCategoryKey,
  POI_CATEGORIES,
} from "../components/NeighbourhoodTab/NeighbourhoodTab";

const SEARCH_BOX_BASE = "https://api.mapbox.com/search/searchbox/v1";

const TRANSPORT_CATEGORIES = [
  "transit_station",
  "subway_station",
  "bus_stop",
] as const;

function bboxAround(lon: number, lat: number, km = 1.5): string {
  const latDelta = km / 111;
  const lonDelta = km / (111 * Math.cos((lat * Math.PI) / 180));
  const minLon = lon - lonDelta;
  const minLat = lat - latDelta;
  const maxLon = lon + lonDelta;
  const maxLat = lat + latDelta;
  return `${minLon},${minLat},${maxLon},${maxLat}`;
}

function mapFeature(f: Record<string, unknown>): Poi {
  const props = (f.properties ?? {}) as Record<string, unknown>;
  return {
    name: String(props.name ?? f.text ?? "Point of Interest"),
    address: String(
      props.full_address ?? props.place_formatted ?? f.place_name ?? ""
    ) || undefined,
    distance: typeof props.distance === "number" ? props.distance : undefined,
  };
}

async function fetchCategory(
  mapboxCategory: string,
  lon: number,
  lat: number,
  token: string,
  limit = 10
): Promise<Poi[]> {
  const params = new URLSearchParams({
    proximity: `${lon},${lat}`,
    bbox: bboxAround(lon, lat),
    limit: String(limit),
    country: "GB",
    access_token: token,
  });

  const res = await fetch(
    `${SEARCH_BOX_BASE}/category/${mapboxCategory}?${params}`
  );
  if (!res.ok) {
    console.warn(`POI category ${mapboxCategory} failed: ${res.status}`);
    return [];
  }
  const data = await res.json();
  const features = Array.isArray(data.features) ? data.features : [];
  return features.map((f: Record<string, unknown>) => mapFeature(f));
}

function dedupePois(items: Poi[]): Poi[] {
  const seen = new Set<string>();
  const out: Poi[] = [];
  for (const item of items) {
    const key = item.name.toLowerCase().trim();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out.sort(
    (a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity)
  );
}

async function fetchTransportPois(
  lon: number,
  lat: number,
  token: string
): Promise<Poi[]> {
  const batches = await Promise.all(
    TRANSPORT_CATEGORIES.map((cat) => fetchCategory(cat, lon, lat, token, 8))
  );
  return dedupePois(batches.flat()).slice(0, 12);
}

export async function fetchNearbyPois(
  lon: number,
  lat: number,
  token: string = MAPBOX_TOKEN
): Promise<Record<PoiCategoryKey, Poi[]>> {
  const next = { ...EMPTY_POIS_BY_CATEGORY };

  const standardCategories = POI_CATEGORIES.filter(
    (c) => c.key !== "transport"
  );

  const results = await Promise.all(
    standardCategories.map(async ({ key, mapboxCategory }) => {
      const items = await fetchCategory(mapboxCategory, lon, lat, token, 8);
      return [key, items] as const;
    })
  );

  for (const [key, items] of results) {
    next[key] = items;
  }

  next.transport = await fetchTransportPois(lon, lat, token);

  return next;
}
